import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, query, updateDoc, where } from "firebase/firestore";
import { CuratedTeacher, StudentTeacher } from "models";
import { db } from "services";
import { dbCollections } from "utilities";

/**
 * Retrieves a list of all alumni from PeopleGrove, using the map view type.
 * @returns A Promise containing the list of all PeopleGrove alumni.
 */
export async function getAllStudentTeachers(): Promise<StudentTeacher[]> {
  const q = query(
    collection(db, dbCollections.memberInfo), 
    where("type", "==", "student-teacher")
  );
  const snipshot = await getDocs(q);
  return snipshot.docs.map(doc => {
    return { ...doc.data(), id: doc.id } as StudentTeacher;
  });
}

/**
 * Retrieves more detailed data for one specific student-teacher on PeopleGrove.
 * @param username The username of a specific PeopleGrove student-teacher.
 * @returns A more detailed blob of data for a specific student-teacher on PeopleGrove.
 */
export async function getStudentTeacher(id: string): Promise<StudentTeacher | undefined> {
  const docRef = doc(db, dbCollections.memberInfo, id);
  const snapshot = await getDoc(docRef);
  return snapshot.exists() ? ({ ...snapshot.data(), id: snapshot.id } as StudentTeacher) : undefined;
}

/**
 * Retrieves a specific alum from the database as a CuratedAlum.
 * @param id The ID of the alum to retrieve.
 * @returns A Promise containing a specific alum, or undefined if the ID doesn't exist.
 */
export async function getStudentTeacherStory(id: string): Promise<CuratedTeacher | undefined> {
  const docRef = doc(db, dbCollections.memberInfo, id);
  const snapshot = await getDoc(docRef);
  return snapshot.exists() ? ({ ...snapshot.data(), id: snapshot.id } as CuratedTeacher) : undefined;
}

/**
 * Updates an existing student teacher in the database. Some or all fields may
 * be updated, depending on what's included in the newStudentTeacher param.
 * @param id The ID of the student teacher to update.
 * @param newStudentTeacher The updated version of the student teacher.
 * @returns A Promise representing the status of the update.
 */
export async function updateStudentTeacherStory(
  id: string,
  newStudentTeacher: CuratedTeacher,
): Promise<void> {
  const docRef = doc(db, dbCollections.memberInfo, id);
  return updateDoc(docRef, { ...newStudentTeacher });
}

/**
 * Adds a new alum to the database and returns the id of the created document.
 * @param newAlum The alum to add.
 * @returns A Promise containing the ID of the added alum.
 */
export async function addStudentTeacherStory(newStudentTeacher: CuratedTeacher): Promise<string> {
  const colRef = collection(db, dbCollections.memberInfo);
  const snapshot = await addDoc(colRef, newStudentTeacher);
  return snapshot.id;
}

/**
 * Deletes an item from the database.
 * @param id The ID of the item to delete.
 * @returns A Promise representing the status of the delete.
 */
export async function deleteStory(id: string): Promise<void> {
  const docRef = doc(db, dbCollections.memberInfo, id);
  return deleteDoc(docRef);
}
